import type { ComponentType } from 'react'
import { useEffect, useMemo, useState, type FC } from 'react'

import type { AssetType } from '@skytech/types'
import {
  AssetFallbackIcon,
  BasicTable,
  INITIAL_PER_PAGE_LIMIT,
  Input,
  LoaderWrapper,
  MessagePopup,
  StyledText,
  Toggle,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { ASSET_TYPES, getWhiteLabelForDealerStyle, useDebounce } from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { CellContext, ColumnDef, PaginationState, SortingState } from '@tanstack/react-table'
import CloseIcon from 'mdi-react/CloseIcon'
import { twJoin } from 'tailwind-merge'

import type { RelatedProductResponse } from '@/api/products'
import { productsResources, type RelatedProduct } from '@/api/products'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Highlight from '@/components/util/Highlight'
import { useSearchParamsByKey } from '@/hooks/searchParams'

import { styleBorderByWhiteLabel } from '../ProductsPage'

import { FiltersButton } from './FiltersButton'
import { RelatedCategoryList } from './RelatedCatogoryList'

const MAX_SELECTIONS = 100

type SelectionsMap = { [id: number]: number[] }

export const RelatedProducts: FC<{
  selectedProductId: number
  setNumberOfRelated: (value: number | undefined) => void
}> = ({ selectedProductId, setNumberOfRelated }) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const [selectedProductIds, setSelectedProductIds] = useState<SelectionsMap>({})
  const [savedSelections, setSavedSelections] = useState<SelectionsMap>({})

  const [showSelectedFirst, setShowSelectedFirst] = useState(true)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const [querySearchRelated, setQuerySearchRelated] = useSearchParamsByKey('search-related')
  const [searchRelatedFilter, setSearchRelatedFilter] = useState(querySearchRelated || '')
  const debouncedSearchTerm = useDebounce(searchRelatedFilter, 500)

  const [categories, setCategories] = useState<string[]>([])
  const [relatedSelectedCategories, setRelatedSelectedCategories] = useState<string[]>(
    categories || []
  )
  const debouncedRelatedCategories = useDebounce(relatedSelectedCategories, 1500)

  const [sorting, setSorting] = useState<SortingState>([])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PER_PAGE_LIMIT,
  })

  const handleOnSuccessDataUpdate = (data: RelatedProductResponse) => {
    if (data.pagination.totalCount === 0) {
      setIsAllSelected(false)
    }
    if (!selectedProductId || !data || data.pagination.totalCount === 0) return

    const currentlySelected = selectedProductIds[selectedProductId] ?? []
    const savedPerCurrentPage = data.items.filter(item => item.selected).map(item => item.id)
    const filterSaved =
      savedSelections[selectedProductId]?.length > 0
        ? savedPerCurrentPage.filter(id => !savedSelections[selectedProductId]?.includes(id))
        : savedPerCurrentPage

    const mergedSaved = Array.from(
      new Set([...(savedSelections[selectedProductId] || []), ...filterSaved])
    )
    setSavedSelections({ [selectedProductId]: mergedSaved })

    // Create a unique list of selected IDs
    const mergedSelected = Array.from(new Set([...currentlySelected, ...filterSaved]))
    setSelectedProductIds({ [selectedProductId]: mergedSelected })

    const isAllSelected = data.items.every(item => mergedSelected.includes(item.id))
    setIsAllSelected(isAllSelected)

    setNumberOfRelated(data.numberOfRelatedProducts)
  }

  const queryKey = queries.relatedProducts({
    productId: selectedProductId,
    sorting: {
      sortBy: sorting.length === 0 ? undefined : (String(sorting[0]?.id) as keyof RelatedProduct),
      sort: sorting.length === 0 ? undefined : sorting[0]?.desc ? 'desc' : 'asc',
    },
    columnFilters: {
      keyword: querySearchRelated || undefined,
      category: categories.length > 0 ? categories : undefined,
    },
    pagination: {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    },
    selectedFirst: showSelectedFirst,
  })

  const queryFn = () =>
    productsResources.getRelatedProducts({
      productId: selectedProductId,
      sorting: {
        sortBy: sorting.length === 0 ? undefined : (String(sorting[0]?.id) as keyof RelatedProduct),
        sort: sorting.length === 0 ? undefined : sorting[0]?.desc ? 'desc' : 'asc',
      },
      columnFilters: {
        keyword: querySearchRelated || undefined,
        category: categories.length > 0 ? categories : undefined,
      },
      pagination: {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      },
      selectedFirst: showSelectedFirst,
    })

  const { data, isLoading, refetch } = useQuery(queryKey, queryFn, {
    onSuccess(data) {
      handleOnSuccessDataUpdate(data)
    },
    enabled: Boolean(selectedProductId),
  })

  // Clear filters when changing main product
  useEffect(() => {
    setSearchRelatedFilter('')
    setShowSelectedFirst(true)
    setRelatedSelectedCategories([])
    setSavedSelections({ [selectedProductId]: [] })
    setIsAllSelected(false)
    if (data) {
      // Reapply cached selections from the server response (restore selected items when main product changes)
      // to avoid refetch() call here which would double-fetch on main product change
      handleOnSuccessDataUpdate(data)
    }
  }, [selectedProductId])

  useEffect(() => {
    setSearchRelatedFilter(querySearchRelated || '')
  }, [querySearchRelated])

  useEffect(() => {
    setQuerySearchRelated(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearchRelated])

  useEffect(() => {
    setRelatedSelectedCategories(categories || [])
  }, [categories])

  useEffect(() => {
    setCategories(debouncedRelatedCategories)
  }, [debouncedRelatedCategories, setCategories])

  const productColumns = useMemo<ColumnDef<RelatedProduct>[]>(
    () => [
      {
        header: 'Product name',
        accessorKey: 'productName',
        cell: info => NameCell(info, searchRelatedFilter),
      },
    ],
    [searchRelatedFilter]
  )

  const updateRelatedProductsMutation = useMutation(productsResources.updateRelatedProducts, {
    onError() {
      setErrorPopupMessage({
        text: translate("We couldn't update relations for this product. Please try again later."),
        title: translate('Update product relations error.'),
      })
    },
    onSettled() {
      setSelectedProductIds({ [selectedProductId]: [] })
      setSavedSelections({ [selectedProductId]: [] })
      setIsAllSelected(false)
      refetch()
    },
  })

  const getAddRemoveProductList = () => {
    const selectedIds = selectedProductIds[selectedProductId] ?? []
    const saved = savedSelections[selectedProductId] ?? []
    const addProducts: number[] = selectedIds.filter(id => !saved.includes(id))
    const removeProducts: number[] = saved.filter(id => !selectedIds.includes(id))
    return { addProducts, removeProducts }
  }

  const isSaveBtnDisabled = (add?: number[], remove?: number[]): boolean => {
    if (!selectedProductId) return true
    let { addProducts, removeProducts } = { addProducts: add, removeProducts: remove }
    if (!add && !remove) {
      const list = getAddRemoveProductList()
      addProducts = list.addProducts
      removeProducts = list.removeProducts
    }
    if (!addProducts || !removeProducts) return true
    if (addProducts.length > MAX_SELECTIONS) {
      return true
    }
    return Boolean(addProducts.length === 0 && removeProducts.length === 0)
  }

  const updateRelatedProducts = () => {
    const { addProducts, removeProducts } = getAddRemoveProductList()
    if (isSaveBtnDisabled(addProducts, removeProducts)) return

    updateRelatedProductsMutation.mutate({
      productId: selectedProductId,
      data: { addProducts, removeProducts },
    })
  }

  const toggleFilterSelection = (value: string) => {
    const isSelected = relatedSelectedCategories.includes(value)

    const newSelected = isSelected
      ? relatedSelectedCategories.filter(category => category !== value)
      : [...relatedSelectedCategories, value]

    setRelatedSelectedCategories(newSelected)
  }

  const toggleProductSelection = (id?: number) => {
    if (!id || !selectedProductId) return
    const selectedProducts = selectedProductIds[selectedProductId] || []
    const { addProducts } = getAddRemoveProductList()

    if (selectedProducts?.includes(id)) {
      const filteredList = selectedProducts.filter(pid => pid !== id)
      // Deselect
      setSelectedProductIds({
        ...selectedProductIds,
        [selectedProductId]: filteredList,
      })
      const isAllSelected = data?.items.every(item => filteredList.includes(item.id)) || false
      setIsAllSelected(isAllSelected)
    } else if (addProducts.length >= MAX_SELECTIONS) {
      setErrorPopupMessage({
        text: translate(
          'You can select up to {MAX_SELECTIONS} new related products per save. Save your current selections and try again.',
          { MAX_SELECTIONS: MAX_SELECTIONS.toString() }
        ),
        title: translate('Selection limit reached'),
      })
      return
    } else {
      const newSelectedProducts = [...selectedProducts, id]
      // Select
      setSelectedProductIds({
        ...selectedProductIds,
        [selectedProductId]: newSelectedProducts,
      })
      const isAllSelected =
        data?.items.every(item => newSelectedProducts.includes(item.id)) || false
      setIsAllSelected(isAllSelected)
    }
  }

  const toggleSelectAll = (value: boolean) => {
    if (!selectedProductId || !data || data?.pagination.totalCount === 0) return

    setIsAllSelected(value)
    const selectedProducts = selectedProductIds[selectedProductId] || []
    const { addProducts } = getAddRemoveProductList()

    if (value && data) {
      // Select all (up to MAX_SELECTIONS) per current page
      const addNewSelections = data.items
        .map(item => item.id)
        .filter(id => !selectedProducts.includes(id))
        .slice(0, MAX_SELECTIONS - addProducts.length)
      const newSelectedProducts = [...selectedProducts, ...addNewSelections]
      setSelectedProductIds({
        ...selectedProductIds,
        [selectedProductId]: newSelectedProducts,
      })
    } else if (!value && data) {
      // Deselect all per current page
      const filteredList = selectedProducts.filter(
        pid => !data.items.map(item => item.id).includes(pid)
      )
      setSelectedProductIds({
        ...selectedProductIds,
        [selectedProductId]: filteredList,
      })
    }
  }

  return (
    <Container
      className={twJoin(
        'mx-0 h-[55rem] rounded-lg border-2 border-solid px-0 text-center shadow-lg',
        styleBorderByWhiteLabel[whiteLabel] || 'border-skytech-orange/40'
      )}
    >
      <div className="relative flex h-full flex-col justify-between gap-2">
        {/* Header */}
        <div className="mx-2 flex flex-col items-start justify-between gap-2">
          <div className="flex w-full items-center justify-between">
            <div className="flex basis-1/2 flex-col gap-2">
              <StyledText size="large" colorMode="black" className="m-0 text-start">
                <Translation id="Select related products" />
              </StyledText>
              {data && data.relatedCategories && data.relatedCategories.length > 0 && (
                <RelatedCategoryList
                  relatedCategories={
                    relatedSelectedCategories.length > 0
                      ? relatedSelectedCategories
                      : data.relatedCategories
                  }
                />
              )}
            </div>
            <Input
              id="search-related"
              type="text"
              className="w-auto"
              value={searchRelatedFilter}
              placeholder={translate('Search')}
              onChange={e => setSearchRelatedFilter(e.target.value)}
              RightElement={
                <Button
                  variant="tertiary"
                  className="m-0 bg-transparent p-0"
                  isIconRight
                  Icon={<CloseIcon />}
                  onClick={() => setSearchRelatedFilter('')}
                />
              }
            />
          </div>

          {data && (
            <div className="flex h-full w-full basis-1/2 flex-row items-center justify-between gap-2">
              {data && data.pagination.totalCount > 0 && (
                <Toggle
                  id="show-selected-first"
                  label={<Translation id="Show selected first" />}
                  checked={showSelectedFirst}
                  onChange={() => setShowSelectedFirst(!showSelectedFirst)}
                />
              )}

              <div className="flex items-center gap-4">
                {data && data.pagination.totalCount > 0 && (
                  <StyledText colorMode="gray" size="small" className="m-0">
                    <Translation id="Selected:" className="capitalize" />{' '}
                    {(data?.numberOfRelatedProducts || 0) -
                      (savedSelections[selectedProductId]?.length || 0) +
                      (selectedProductIds[selectedProductId]?.length || 0)}
                  </StyledText>
                )}

                {data.relatedCategories && data.relatedCategories.length > 1 && (
                  <FiltersButton
                    options={data.relatedCategories.map(category => ({
                      value: category,
                      label: <Translation id={category} />,
                      isSelected: relatedSelectedCategories.includes(category),
                    }))}
                    onClick={toggleFilterSelection}
                    onClear={() => setRelatedSelectedCategories([])}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <LoaderWrapper isLoading={isLoading} whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}>
          <BasicTable
            isMultipleSelection
            dataWithPagination={data}
            columns={productColumns}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            selectedRows={selectedProductIds[selectedProductId] || []}
            onTrClick={toggleProductSelection}
            selectAll={{
              isAllSelected,
              handleSelectAll: toggleSelectAll,
            }}
            wrapperClassName="h-full overflow-y-auto"
            TranslationComponent={Translation as ComponentType}
          />

          {/* Footer with action button */}
          <div className="mb-2 flex justify-end">
            <Button
              variant="primary"
              disabled={updateRelatedProductsMutation.isLoading || isSaveBtnDisabled()}
              onClick={updateRelatedProducts}
            >
              {updateRelatedProductsMutation.isLoading ? (
                <Translation id="Saving changes..." />
              ) : (
                <Translation id="Save changes" />
              )}
            </Button>
          </div>
        </LoaderWrapper>
      </div>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />
    </Container>
  )
}

function NameCell(info: CellContext<RelatedProduct, unknown>, textSearch: string) {
  const row = info.row.original
  const assetType =
    row.category && ASSET_TYPES.includes(row.category as AssetType)
      ? (row.category as AssetType)
      : 'OTHER'
  return (
    <div className="flex flex-row gap-2">
      <ImageWithIconFallback
        alt="asset"
        src={row.imageUrl || ''}
        className="shadow-basic flex h-[3.7rem] w-[3.7rem] min-w-[3.7rem] max-w-full items-center justify-center rounded-full bg-white p-1 shadow transition duration-200 ease-in-out max-[500px]:h-12 max-[500px]:w-12 sm:h-[3.7rem] sm:w-[3.7rem]"
        fallbackIcon={<AssetFallbackIcon assetType={assetType} />}
      />
      <div className="flex flex-col justify-start gap-0.5 text-start">
        <StyledText
          colorMode="black"
          size="regular-plus"
          fontWeight="standard"
          className="font-semibold"
        >
          <Highlight pattern={textSearch} text={row.productName || row.articleName} />
        </StyledText>

        {row.manufacturerProductNo && (
          <StyledText colorMode="gray" size="small" fontWeight="standard">
            <Translation id="Manufacture nmb.:" />{' '}
            <span className="font-semibold">
              <Highlight pattern={textSearch} text={row.manufacturerProductNo} />
            </span>
          </StyledText>
        )}

        {row.articleNo === row.vendorUniqueId && (
          <StyledText colorMode="gray" size="small" fontWeight="standard">
            <Translation id="Article nmb.:" />{' '}
            <span className="font-semibold">
              <Highlight pattern={textSearch} text={row.articleNo} />
            </span>
          </StyledText>
        )}

        {row.articleNo !== row.vendorUniqueId && (
          <>
            {row.articleNo && (
              <StyledText colorMode="gray" size="small" fontWeight="standard">
                <Translation id="Article nmb.:" />{' '}
                <span className="font-semibold">
                  <Highlight pattern={textSearch} text={row.articleNo} />
                </span>
              </StyledText>
            )}
            {row.vendorUniqueId && (
              <StyledText colorMode="gray" size="small" fontWeight="standard">
                <Translation id="Vendor unique ID:" />{' '}
                <span className="font-semibold">
                  {' '}
                  <Highlight pattern={textSearch} text={row.vendorUniqueId} />
                </span>
              </StyledText>
            )}
          </>
        )}

        {row.category && (
          <StyledText colorMode="gray" size="small" fontWeight="standard">
            <Translation id={row.category} />
          </StyledText>
        )}
      </div>
    </div>
  )
}
